import { inject, Injectable } from '@angular/core';
import { CollaborationAttachmentsDataService } from '@ffd/api-client';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { FfdComponentStore, multiDownload } from '@libs/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { concat, EMPTY, forkJoin, interval, map, Observable, of, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { AttachmentVM } from '../../components';

export interface DownloadAttachmentsState {
  patientId: string;
  selectedAttachments: AttachmentVM[];
  selectedAttachmentIds: string[];
  messageId?: string;
  messageInDownloadMode?: string;
  saveToPmsMode?: boolean;
  saveToDesktopMode?: boolean;
  saveToPmsProgress: number;
  saveToPmsSuccess: boolean;
}

export interface DownloadAttachmentsVM {
  selectedAttachments: AttachmentVM[];
  selectedAttachmentIds: string[];
  messageInDownloadMode?: string;
  saveToPmsMode?: boolean;
  saveToDesktopMode?: boolean;
  saveToPmsProgress: number;
  savetoPmsSuccess: boolean;
}

export const initialState: DownloadAttachmentsState = {
  patientId: '',
  selectedAttachments: [],
  selectedAttachmentIds: [],
  saveToPmsProgress: 0,
  saveToPmsSuccess: false,
  messageId: undefined,
  messageInDownloadMode: undefined,
  saveToDesktopMode: false,
  saveToPmsMode: false,
};

export const SAVE_TO_PMS_PROGRESS = 5;
export const SAVE_TO_PMS_PROGRESS_DIVIDER = 3;
export const SAVE_TO_PMS_SUCCESS_INDICATOR_TIMEOUT = 3000;

@Injectable({ providedIn: 'root' })
export class DownloadAttachmentsService extends FfdComponentStore<DownloadAttachmentsState, DownloadAttachmentsVM> {
  private readonly collaborationAttachmentDataService = inject(CollaborationAttachmentsDataService);
  private readonly cancelRequest$ = new Subject<void>();
  private readonly tagPusherService = inject(TagPusherService);

  constructor() {
    super(initialState);
  }

  public override init(): void {}

  public readonly setPatientId = this.updater(
    (state, patientId: string): DownloadAttachmentsState => ({
      ...state,
      patientId,
    }),
  );
  public readonly setMessageId = this.updater(
    (state, messageId: string): DownloadAttachmentsState => ({
      ...state,
      messageId,
    }),
  );

  public readonly setMessageInDownloadMode = this.updater(
    (state, messageId: string): DownloadAttachmentsState => ({
      ...state,
      messageInDownloadMode: messageId,
      saveToDesktopMode: true,
    }),
  );

  public readonly resetMessageInDownloadMode = this.updater((state): DownloadAttachmentsState => {
    return initialState;
  });

  public readonly setAttachments = this.updater(
    (state, attachments: AttachmentVM[]): DownloadAttachmentsState => ({
      ...state,
      selectedAttachments: attachments,
      selectedAttachmentIds: attachments.map((attachment) => attachment.id),
    }),
  );

  public cancelSavetoPms(): void {
    this.cancelRequest$.next();
    this.patchState(initialState);
  }

  public readonly setSaveToPmsMode = this.updater(
    (state, saveToPmsMode: boolean): DownloadAttachmentsState => ({
      ...state,
      saveToPmsMode,
    }),
  );

  public readonly setSaveToDesktopMode = this.updater(
    (state, saveToDesktopMode: boolean): DownloadAttachmentsState => ({
      ...state,
      saveToDesktopMode,
    }),
  );

  public readonly saveToPMS = this.effect(($: Observable<void>) => {
    return $.pipe(
      concatLatestFrom(() => this.select((state) => state.selectedAttachments)),
      switchMap(([_, attachmentIds]) => {
        this.tagPusherService.pushTag({
          event: TagEvent.CollaborationPatientChartSaveStarted,
        });
        if (attachmentIds.length === 0) {
          this.patchState(initialState);
          return of(EMPTY);
        }
        const numbersObservable$ = interval(1000).pipe(
          startWith(0),
          take(SAVE_TO_PMS_PROGRESS),
          map((i) => i + 1),
        );
        return concat(numbersObservable$, of('url')).pipe(
          takeUntil(this.cancelRequest$),
          map((progress) => {
            if (typeof progress === 'number') {
              this.patchState({ saveToPmsProgress: (progress / SAVE_TO_PMS_PROGRESS_DIVIDER) * 100 });
            }

            if (typeof progress === 'string') {
              this.tagPusherService.pushTag({
                event: TagEvent.CollaborationSaveToPmsFinished,
              });
              this.setSaveToPmsSuccess(true);
            }
          }),
        );
      }),
    );
  });

  public readonly setSaveToPmsSuccess = this.effect(($: Observable<boolean>) => {
    return $.pipe(
      switchMap((success) => {
        this.patchState({ saveToPmsSuccess: success });

        return interval(SAVE_TO_PMS_SUCCESS_INDICATOR_TIMEOUT).pipe(
          take(1),
          map(() => {
            this.patchState({
              saveToPmsProgress: 0,
              messageInDownloadMode: undefined,
              selectedAttachments: [],
              selectedAttachmentIds: [],
              saveToPmsSuccess: false,
            });
          }),
        );
      }),
    );
  });

  public readonly downloadAttachments = this.effect(($: Observable<void>) =>
    $.pipe(
      concatLatestFrom(() => this.select((state) => state.selectedAttachments)),
      switchMap(([_, attachments]) =>
        forkJoin(
          attachments.map((attachment) => {
            if (attachment.file) {
              return of(URL.createObjectURL(attachment.file));
            }
            return this.collaborationAttachmentDataService.getDownloadLinkForOriginalAttachment(attachment.id, 'body', true).pipe(
              map((response) => {
                return response.url;
              }),
            );
          }),
        ),
      ),
      tap(async (urls) => {
        try {
          await multiDownload(urls);
          this.tagPusherService.pushTag({
            event: TagEvent.CollaborationDownloadFinished,
            patient_id: this.state().patientId,
          });
        } catch (error) {
          console.error('Error during download', error);
        }
      }),
    ),
  );

  public override mapStateToViewModel(state: DownloadAttachmentsState): DownloadAttachmentsVM {
    return {
      messageInDownloadMode: state.messageInDownloadMode,
      selectedAttachments: state.selectedAttachments,
      selectedAttachmentIds: state.selectedAttachmentIds,
      saveToPmsProgress: state.saveToPmsProgress,
      savetoPmsSuccess: state.saveToPmsSuccess,
      saveToDesktopMode: state.saveToDesktopMode,
      saveToPmsMode: state.saveToPmsMode,
    };
  }
}
